/**
 * wizard-lib.ts — Phần THUẦN của luồng tạo nhân viên AI 5 bước (`/ai/nhan-vien/moi`): trạng thái từng bước (theo dữ liệu
 * thật, không tick sẵn), mẫu giờ làm ↔ các trường lịch của bot, kênh đang do nhân viên AI nào phụ trách, danh sách kiểm
 * tra trước khi ra mắt (từ `GET /ai/bots/:id/status`), gợi ý khi chạy thử chưa đạt, căn chỉnh checklist trang Bắt đầu.
 *
 * Lịch trên máy chủ: khung NGOÀI GIỜ `scheduleStartHour → scheduleEndHour` (mặc định 18 → 8); `scheduleDaytimeMode`
 * (suggest | manual) = trong giờ; `scheduleNighttimeMode` (auto | suggest) = ngoài giờ; tắt lịch → `defaultMode` cả ngày.
 */
import { i18n } from '@/lib/i18n'
import type { AiBot, AiBotStatus, AiMode } from '@/hooks/use-ai'
import type { StartStep } from '@/lib/ai-khung'
import { isDefaultBot } from '@/lib/ai-khung'

// ── Đường dẫn ─────────────────────────────────────────────────────────

export const WIZARD_PATH = '/ai/nhan-vien/moi'

/** Màn chi tiết nhân viên AI (khung ba tầng; máy chủ dùng cùng đường ở `fix.path`). `section` → `#<mục>`. */
export function botDetailPath(id: string, section?: string | null): string {
  return `/ai/nhan-vien/${id}${section ? `#${section}` : ''}`
}

/** Link vào lại luồng tạo cho một bản nháp. */
export function wizardResumePath(botId: string, step?: number): string {
  return `${WIZARD_PATH}?bot=${encodeURIComponent(botId)}${step ? `&buoc=${step}` : ''}`
}

/** Hộp thư lọc theo nhân viên AI (Hộp thư cần đọc `?filter=ai&aiBot=` — xem báo cáo). */
export function inboxForBotPath(botId: string): string {
  return `/conversations?filter=ai&aiBot=${encodeURIComponent(botId)}`
}

// ── Các bước ─────────────────────────────────────────────────────────

export type WizardStepKey = 'mau' | 'kien-thuc' | 'chay-thu' | 'kenh' | 'ra-mat'

export const WIZARD_STEPS: Array<{ n: number; key: WizardStepKey; title: string; optional?: boolean }> = [
  { n: 1, key: 'mau', get title() { return i18n.t('aiWizard:lib.steps.mau') } },
  { n: 2, key: 'kien-thuc', get title() { return i18n.t('aiWizard:lib.steps.kienThuc') }, optional: true },
  { n: 3, key: 'chay-thu', get title() { return i18n.t('aiWizard:lib.steps.chayThu') } },
  { n: 4, key: 'kenh', get title() { return i18n.t('aiWizard:lib.steps.kenh') } },
  { n: 5, key: 'ra-mat', get title() { return i18n.t('aiWizard:lib.steps.raMat') } },
]

/** `?buoc=` → 1..5 (lạ → 1). Chưa có bản nháp thì luôn bước 1. */
export function stepFromParam(v: string | null | undefined, hasBot: boolean): number {
  if (!hasBot) return 1
  const n = Number(v)
  return Number.isInteger(n) && n >= 1 && n <= 5 ? n : 2
}

export type StepState = 'done' | 'skipped' | 'todo'

export interface StepFacts {
  hasBot: boolean
  /** Nhân viên AI đã gắn kho tri thức có nội dung. */
  knowledgeReady: boolean
  /** Số lượt chạy thử đã có (thành công). */
  testTurns: number
  /** Số kênh đã giao cho nhân viên AI. */
  channelCount: number
  /** Đang trả lời khách (status.answering + bot bật). */
  live: boolean
  /** Bước xa nhất người dùng đã tới. */
  maxVisited: number
}

/** Trạng thái từng bước — chỉ "xong" khi có dữ liệu thật; bước tuỳ chọn đã đi qua mà chưa làm = "bỏ qua". */
export function stepStates(f: StepFacts): Record<WizardStepKey, StepState> {
  const knowledge: StepState = f.knowledgeReady ? 'done' : f.maxVisited > 2 ? 'skipped' : 'todo'
  return {
    mau: f.hasBot ? 'done' : 'todo',
    'kien-thuc': f.hasBot ? knowledge : 'todo',
    'chay-thu': f.hasBot && f.testTurns > 0 ? 'done' : 'todo',
    kenh: f.hasBot && f.channelCount > 0 ? 'done' : 'todo',
    'ra-mat': f.hasBot && f.live ? 'done' : 'todo',
  }
}

/** Kho tri thức nhân viên AI tra ([] = mọi kho của tổ chức). */
export function botKnowledgeBaseIds(bot: Pick<AiBot, 'toolsJson'> | null | undefined): string[] {
  const ids = bot?.toolsJson?.search_knowledge?.guardrail?.knowledgeBaseIds
  return Array.isArray(ids) ? ids : []
}

/**
 * Nhân viên AI có kiến thức để tra: công cụ tra kiến thức bật + kho được gắn có nội dung (tài liệu / hỏi đáp).
 * Mặc định phải gắn ĐÍCH DANH kho (luồng tạo: không coi "mọi kho" là đã dạy); `allBases` = coi [] (mọi kho) là gắn hết.
 */
export function knowledgeReady(
  bot: Pick<AiBot, 'toolsJson'> | null | undefined,
  bases: Array<{ id: string; articleCount: number; qaCount: number }> | null | undefined,
  opts: { allBases?: boolean } = {},
): boolean {
  if (!bot || bot.toolsJson?.search_knowledge?.enabled === false || !bases) return false
  const ids = botKnowledgeBaseIds(bot)
  if (!ids.length) return !!opts.allBases && bases.some((b) => b.articleCount + b.qaCount > 0)
  return bases.some((b) => ids.includes(b.id) && b.articleCount + b.qaCount > 0)
}

/** toolsJson mới khi đổi các kho gắn với nhân viên AI (giữ phần khác). */
export function withKnowledgeBases<T extends AiBot['toolsJson']>(tools: T, ids: string[]): NonNullable<AiBot['toolsJson']> {
  const sk = tools?.search_knowledge
  return {
    ...(tools ?? {}),
    search_knowledge: {
      enabled: sk?.enabled ?? true,
      guardrail: { ...(sk?.guardrail ?? {}), categoryIds: sk?.guardrail?.categoryIds ?? [], knowledgeBaseIds: [...new Set(ids)] },
    },
  }
}

// ── Giờ làm ──────────────────────────────────────────────────────────

export type HoursPreset = 'q4' | 'auto247' | 'custom'

export interface CustomHours {
  /** Giờ bắt đầu làm việc (0..23). */
  workStart: number
  /** Giờ hết giờ làm việc (0..23). */
  workEnd: number
  inHoursMode: 'suggest' | 'manual'
  afterHoursMode: 'auto' | 'suggest'
}

export interface ScheduleFields {
  scheduleEnabled: boolean
  scheduleStartHour: number
  scheduleEndHour: number
  scheduleDaytimeMode: 'suggest' | 'manual'
  scheduleNighttimeMode: 'auto' | 'suggest'
  defaultMode: AiMode
}

export const DEFAULT_CUSTOM_HOURS: CustomHours = { workStart: 8, workEnd: 18, inHoursMode: 'suggest', afterHoursMode: 'auto' }

export const HOURS_PRESETS: Array<{ value: HoursPreset; title: string; desc: string }> = [
  {
    value: 'q4',
    get title() { return i18n.t('aiWizard:lib.presets.q4.title') },
    get desc() { return i18n.t('aiWizard:lib.presets.q4.desc') },
  },
  { value: 'auto247', get title() { return i18n.t('aiWizard:lib.presets.auto247.title') }, get desc() { return i18n.t('aiWizard:lib.presets.auto247.desc') } },
  { value: 'custom', get title() { return i18n.t('aiWizard:lib.presets.custom.title') }, get desc() { return i18n.t('aiWizard:lib.presets.custom.desc') } },
]

/** Mẫu giờ làm → các trường lịch gửi `PUT /ai/bots/:id`. Tuỳ chỉnh: giờ làm A→B ⇒ khung ngoài giờ máy chủ B→A. */
export function scheduleFields(preset: HoursPreset, custom: CustomHours = DEFAULT_CUSTOM_HOURS): ScheduleFields {
  if (preset === 'auto247') {
    return { scheduleEnabled: false, scheduleStartHour: 18, scheduleEndHour: 8, scheduleDaytimeMode: 'suggest', scheduleNighttimeMode: 'auto', defaultMode: 'auto' }
  }
  const c = preset === 'q4' ? DEFAULT_CUSTOM_HOURS : custom
  return {
    scheduleEnabled: true,
    scheduleStartHour: c.workEnd,
    scheduleEndHour: c.workStart,
    scheduleDaytimeMode: c.inHoursMode,
    scheduleNighttimeMode: c.afterHoursMode,
    defaultMode: 'suggest',
  }
}

/** Lỗi của giờ tuỳ chỉnh (null = hợp lệ). */
export function customHoursProblem(c: CustomHours): string | null {
  const ok = (h: number) => Number.isInteger(h) && h >= 0 && h <= 23
  if (!ok(c.workStart) || !ok(c.workEnd)) return i18n.t('aiWizard:lib.hours.range')
  if (c.workStart === c.workEnd) return i18n.t('aiWizard:lib.hours.different')
  return null
}

type BotSchedule = Partial<Pick<AiBot, 'scheduleEnabled' | 'scheduleStartHour' | 'scheduleEndHour' | 'scheduleDaytimeMode' | 'scheduleNighttimeMode' | 'defaultMode'>>

/** Bot đang lưu → mẫu giờ làm tương ứng (để mở lại bước 4 đúng lựa chọn). Lịch tắt mà cả ngày không phải Tự động → tuỳ chỉnh. */
export function presetOf(bot: BotSchedule): { preset: HoursPreset; custom: CustomHours } {
  if (!bot.scheduleEnabled) {
    return { preset: bot.defaultMode === 'auto' ? 'auto247' : 'custom', custom: { ...DEFAULT_CUSTOM_HOURS } }
  }
  const custom: CustomHours = {
    workStart: bot.scheduleEndHour ?? 8,
    workEnd: bot.scheduleStartHour ?? 18,
    inHoursMode: bot.scheduleDaytimeMode === 'manual' ? 'manual' : 'suggest',
    afterHoursMode: bot.scheduleNighttimeMode === 'suggest' ? 'suggest' : 'auto',
  }
  const isQ4 = custom.workStart === 8 && custom.workEnd === 18 && custom.inHoursMode === 'suggest' && custom.afterHoursMode === 'auto'
  return { preset: isQ4 ? 'q4' : 'custom', custom }
}

const hh = (h: number) => `${String(h).padStart(2, '0')}:00`
const MODE_KEYS = ['manual', 'suggest', 'auto']
const modeText = (m: string | undefined, fallback: 'suggest' | 'auto'): string =>
  i18n.t(`aiWizard:lib.mode.${MODE_KEYS.includes(m ?? '') ? m : fallback}`)

/** "08:00–18:00 AI gợi ý · ngoài giờ AI tự trả lời" / "Cả ngày AI tự trả lời". */
export function hoursSummary(f: BotSchedule): string {
  if (!f.scheduleEnabled) return i18n.t('aiWizard:lib.summary.allDay', { mode: modeText(f.defaultMode ?? 'suggest', 'suggest') })
  const start = f.scheduleEndHour ?? 8
  const end = f.scheduleStartHour ?? 18
  return i18n.t('aiWizard:lib.summary.range', { from: hh(start), to: hh(end), inMode: modeText(f.scheduleDaytimeMode ?? 'suggest', 'suggest'), outMode: modeText(f.scheduleNighttimeMode ?? 'auto', 'auto') })
}

/** Lịch khiến AI không bao giờ tự trả lời / gợi ý: tắt lịch + cả ngày Thủ công. */
export function alwaysManual(f: BotSchedule): boolean {
  return !f.scheduleEnabled && f.defaultMode === 'manual'
}

// ── Kênh ─────────────────────────────────────────────────────────────

export interface ChannelLike {
  id: string
  displayName: string | null
  platform: number
  status?: string | null
  liveStatus?: string | null
  isDisabled?: boolean
}

type BotForChannels = Pick<AiBot, 'id' | 'name' | 'enabled' | 'channelAccountIds' | 'allChannels' | 'isDefault'>

export type ChannelOwnerState = 'mine' | 'other' | 'default' | 'free'

export interface ChannelOption {
  id: string
  name: string
  platform: number
  connected: boolean
  state: ChannelOwnerState
  ownerName: string | null
  /** Dòng ghi chú dưới tên kênh. */
  note: string
}

export function channelConnected(c: ChannelLike): boolean {
  if (c.isDisabled) return false
  const s = c.liveStatus ?? c.status
  return s == null || s === 'connected'
}

/**
 * Kênh để chọn ở bước 4: kênh của chính nhân viên này / đang giao cho nhân viên AI khác ("chọn để chuyển sang") /
 * do nhân viên AI mặc định phục vụ / chưa có ai.
 */
export function channelOptions(channels: ChannelLike[], bots: BotForChannels[], botId: string): ChannelOption[] {
  const me = bots.find((b) => b.id === botId)
  const def = bots.find((b) => isDefaultBot(b) && b.id !== botId)
  return channels
    .filter((c) => !c.isDisabled)
    .map((c) => {
      const name = c.displayName?.trim() || i18n.t('aiWizard:lib.channel.unnamed')
      const connected = channelConnected(c)
      const base = { id: c.id, name, platform: c.platform, connected }
      if ((me?.channelAccountIds ?? []).includes(c.id)) return { ...base, state: 'mine' as const, ownerName: me?.name ?? null, note: '' }
      const other = bots.find((b) => b.id !== botId && (b.channelAccountIds ?? []).includes(c.id))
      if (other) {
        return {
          ...base, state: 'other' as const, ownerName: other.name,
          note: i18n.t(other.enabled ? 'aiWizard:lib.channel.noteOther' : 'aiWizard:lib.channel.noteOtherOff', { name: other.name }),
        }
      }
      if (def) {
        return {
          ...base, state: 'default' as const, ownerName: def.name,
          note: i18n.t(def.enabled ? 'aiWizard:lib.channel.noteDefault' : 'aiWizard:lib.channel.noteDefaultOff', { name: def.name }),
        }
      }
      return { ...base, state: 'free' as const, ownerName: null, note: i18n.t('aiWizard:lib.channel.noteFree') }
    })
}

/** Kênh chuyển từ nhân viên AI khác (để báo trước khi lưu). */
export function stolenChannels(options: ChannelOption[], selected: string[]): ChannelOption[] {
  return options.filter((o) => o.state === 'other' && selected.includes(o.id))
}

// ── Ra mắt: danh sách kiểm tra ───────────────────────────────────────

export type CheckState = 'ok' | 'fail' | 'auto' | 'warn'

export interface LaunchCheck {
  key: string
  label: string
  state: CheckState
  detail: string
  fix?: { label: string; path: string | null; section?: string } | null
}

type Reason = NonNullable<AiBotStatus['reasons']>[number]

const SOURCE_CODES = new Set(['no_credit', 'no_key', 'key_invalid', 'model_not_allowed', 'daily_credit_cap', 'platform_source_disabled', 'source_unavailable', 'turn_quota', 'external_config_missing'])
const ORG_CODES = new Set(['platform_locked', 'org_ai_disabled', 'org_paused'])
const CHANNEL_CODES = new Set(['no_channels', 'channels_disconnected'])
const HANDLED = new Set([...SOURCE_CODES, ...ORG_CODES, ...CHANNEL_CODES, 'bot_disabled', 'unpublished_changes', 'mode_manual', 'bot_offline'])

export interface LaunchInput {
  status: AiBotStatus | null | undefined
  bot: Pick<AiBot, 'id' | 'enabled' | 'onlineStatus' | 'channelAccountIds'> & BotSchedule
  /** Kênh của tổ chức (để biết kênh đã giao có đang kết nối không khi bot còn tắt). */
  channels: ChannelLike[]
}

/**
 * Danh sách kiểm tra trước khi bật: nguồn AI (credit / key) · có kênh đang kết nối · đã phát hành · AI tổ chức không tạm
 * dừng · chế độ không phải thủ công · nhân viên AI bật + trực tuyến, rồi các lý do khác của máy chủ.
 * 'auto' = nút "Bật trả lời khách" tự làm (phát hành, bật nhân viên, bỏ tạm dừng có hỏi). 'fail' = phải sửa trước.
 */
export function launchChecklist(i: LaunchInput): LaunchCheck[] {
  const reasons: Reason[] = i.status?.reasons ?? []
  const has = (code: string) => reasons.find((r) => r.code === code)
  const out: LaunchCheck[] = []

  // Nguồn AI
  const src = reasons.filter((r) => SOURCE_CODES.has(r.code))
  out.push(src.length
    ? { key: 'source', label: i18n.t('aiWizard:lib.check.source.label'), state: 'fail', detail: src.map((r) => r.message).join(' '), fix: src[0].fix ?? null }
    : { key: 'source', label: i18n.t('aiWizard:lib.check.source.label'), state: 'ok', detail: i18n.t('aiWizard:lib.check.source.ok') })

  // Kênh — bot đang tắt thì máy chủ chưa tính kênh phục vụ → tự đếm theo kênh đã giao.
  const mine = i.channels.filter((c) => !c.isDisabled && (i.bot.channelAccountIds ?? []).includes(c.id))
  const live = mine.filter(channelConnected)
  if (!mine.length) {
    out.push({ key: 'channels', label: i18n.t('aiWizard:lib.check.channels.label'), state: 'fail', detail: i18n.t('aiWizard:lib.check.channels.none'), fix: { label: i18n.t('aiWizard:lib.check.channels.fixChoose'), path: null, section: 'kenh' } })
  } else if (!live.length) {
    out.push({ key: 'channels', label: i18n.t('aiWizard:lib.check.channels.label'), state: 'fail', detail: i18n.t('aiWizard:lib.check.channels.allDown'), fix: { label: i18n.t('aiWizard:lib.check.channels.fixReconnect'), path: '/integrations' } })
  } else {
    const down = mine.length - live.length
    out.push({
      key: 'channels', label: i18n.t('aiWizard:lib.check.channels.label'), state: down ? 'warn' : 'ok',
      detail: down ? i18n.t('aiWizard:lib.check.channels.partial', { live: live.length, total: mine.length }) : i18n.t('aiWizard:lib.check.channels.connected', { count: live.length }),
      fix: down ? { label: i18n.t('aiWizard:lib.check.channels.fixReconnect'), path: '/integrations' } : null,
    })
  }

  // Phát hành
  const published = i.status?.publishedVersion != null
  const dirty = !!i.status?.hasUnpublishedChanges
  out.push(published && !dirty
    ? { key: 'published', label: i18n.t('aiWizard:lib.check.published.label'), state: 'ok', detail: i18n.t('aiWizard:lib.check.published.ok', { version: i.status?.publishedVersion }) }
    : { key: 'published', label: i18n.t('aiWizard:lib.check.published.label'), state: 'auto', detail: published ? i18n.t('aiWizard:lib.check.published.dirty') : i18n.t('aiWizard:lib.check.published.first') })

  // AI tổ chức
  const locked = has('platform_locked')
  const off = has('org_ai_disabled')
  const paused = has('org_paused')
  if (locked) out.push({ key: 'org', label: i18n.t('aiWizard:lib.check.org.label'), state: 'fail', detail: locked.message, fix: locked.fix ?? null })
  else if (off) out.push({ key: 'org', label: i18n.t('aiWizard:lib.check.org.label'), state: 'fail', detail: off.message, fix: off.fix ?? null })
  else if (paused) out.push({ key: 'org', label: i18n.t('aiWizard:lib.check.org.label'), state: 'auto', detail: i18n.t('aiWizard:lib.check.org.paused') })
  else out.push({ key: 'org', label: i18n.t('aiWizard:lib.check.org.label'), state: 'ok', detail: i18n.t('aiWizard:lib.check.org.ok') })

  // Chế độ
  if (alwaysManual(i.bot)) {
    out.push({ key: 'mode', label: i18n.t('aiWizard:lib.check.mode.label'), state: 'fail', detail: i18n.t('aiWizard:lib.check.mode.always'), fix: { label: i18n.t('aiWizard:lib.check.mode.fix'), path: null, section: 'gio-lam' } })
  } else if (has('mode_manual')) {
    out.push({ key: 'mode', label: i18n.t('aiWizard:lib.check.mode.label'), state: 'warn', detail: i18n.t('aiWizard:lib.check.mode.now') })
  } else {
    out.push({ key: 'mode', label: i18n.t('aiWizard:lib.check.mode.label'), state: 'ok', detail: hoursSummary(i.bot) + '.' })
  }

  // Bật + trực tuyến
  out.push(i.bot.enabled
    ? { key: 'enabled', label: i18n.t('aiWizard:lib.check.enabled.label'), state: 'ok', detail: i18n.t('aiWizard:lib.check.enabled.ok') }
    : { key: 'enabled', label: i18n.t('aiWizard:lib.check.enabled.label'), state: 'auto', detail: i18n.t('aiWizard:lib.check.enabled.auto') })
  const offline = has('bot_offline') ?? (i.bot.onlineStatus === 'offline' ? { code: 'bot_offline', message: i18n.t('aiWizard:lib.check.online.offline'), fix: null } as Reason : undefined)
  if (offline) out.push({ key: 'online', label: i18n.t('aiWizard:lib.check.online.label'), state: 'auto', detail: i18n.t('aiWizard:lib.check.online.detail', { message: offline.message }) })

  // Lý do khác của máy chủ
  for (const r of reasons) {
    if (HANDLED.has(r.code)) continue
    out.push({ key: `r-${r.code}`, label: r.message, state: r.blocking ? 'fail' : 'warn', detail: '', fix: r.fix ?? null })
  }
  return out
}

export function launchBlocked(items: LaunchCheck[]): boolean {
  return items.some((c) => c.state === 'fail')
}

/** Mục nào của luồng tạo sửa được lỗi này (section của máy chủ → bước). null = mở màn khác. */
export function fixStep(section: string | null | undefined): number | null {
  if (section === 'kenh' || section === 'gio-lam' || section === 'van-hanh') return 4
  return null
}

/**
 * Đường dẫn của nút sửa (khi `fixStep` = null): mục của chính nhân viên AI này → màn chi tiết đúng mục (`#<mục>`); còn lại
 * giữ nguyên đường máy chủ trả. Luồng tạo mở các màn này ở TAB MỚI (open-target-lib) — bước Ra mắt giữ nguyên.
 */
export function launchFixPath(fix: { path?: string | null; section?: string | null }, botId: string): string | null {
  if (!fix.path) return null
  return fix.path.startsWith(`/ai/nhan-vien/${botId}`) && fix.section ? botDetailPath(botId, fix.section) : fix.path
}

// ── Chạy thử: gợi ý khi chưa đạt ─────────────────────────────────────

export interface DislikeHint {
  key: 'kien-thuc' | 'vai-tro'
  label: string
  desc: string
}

/** 👎 → gợi ý mục cần sửa: không tìm thấy nguồn nào → Kiến thức trước; có nguồn → Vai trò & giọng trước. */
export function dislikeHints(turn: { sourceCount: number; handoff?: boolean }): DislikeHint[] {
  const kb: DislikeHint = { key: 'kien-thuc', label: i18n.t('aiWizard:lib.dislike.kbLabel'), desc: turn.sourceCount === 0 ? i18n.t('aiWizard:lib.dislike.kbNone') : i18n.t('aiWizard:lib.dislike.kbWrong') }
  const role: DislikeHint = { key: 'vai-tro', label: i18n.t('aiWizard:lib.dislike.roleLabel'), desc: i18n.t('aiWizard:lib.dislike.roleDesc') }
  return turn.sourceCount === 0 ? [kb, role] : [role, kb]
}

export interface RatingSummary { total: number; up: number; down: number; unrated: number }

export function ratingSummary(turns: Array<{ rating?: 'up' | 'down' | null; error?: string | null }>): RatingSummary {
  const ok = turns.filter((t) => !t.error)
  const up = ok.filter((t) => t.rating === 'up').length
  const down = ok.filter((t) => t.rating === 'down').length
  return { total: ok.length, up, down, unrated: ok.length - up - down }
}

// ── Trang Bắt đầu: cùng tiêu chí với luồng tạo ───────────────────────

type BotForStart = Pick<AiBot, 'id' | 'name' | 'enabled' | 'toolsJson' | 'isDefault' | 'allChannels' | 'status' | 'createdAt'>

/** Bản nháp của luồng tạo: không phải mặc định, đang tắt, chưa từng phát hành. Mới nhất trước. */
export function draftBots<T extends BotForStart>(bots: T[]): T[] {
  return bots
    .filter((b) => !isDefaultBot(b) && !b.enabled && (b.status?.publishedVersion ?? null) == null)
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
}

/**
 * Checklist trang Bắt đầu theo đúng tiêu chí của luồng tạo: "Nạp kiến thức" xong khi một nhân viên AI đã gắn kho có nội
 * dung; "Chạy thử" xong khi đã có lượt thử (ghi trên máy này); có bản nháp → các nút dẫn vào lại luồng tạo đúng bước.
 */
export function alignStartSteps(steps: StartStep[], i: {
  bots: BotForStart[]
  bases: Array<{ id: string; articleCount: number; qaCount: number }> | null | undefined
  testedBotIds: string[]
}): StartStep[] {
  const draft = draftBots(i.bots)[0]
  const kbReady = i.bots.some((b) => knowledgeReady(b, i.bases, { allBases: true }))
  const tested = i.bots.some((b) => i.testedBotIds.includes(b.id))
  return steps.map((s) => {
    switch (s.key) {
      case 'bot':
        return draft ? { ...s, to: wizardResumePath(draft.id), cta: i18n.t('aiWizard:lib.start.continue', { name: draft.name }) } : s
      case 'knowledge':
        return { ...s, done: i.bases ? kbReady : s.done, to: draft ? wizardResumePath(draft.id, 2) : s.to }
      case 'test':
        return { ...s, done: tested, to: draft ? wizardResumePath(draft.id, 3) : s.to }
      case 'assign':
        return draft ? { ...s, to: wizardResumePath(draft.id, 4) } : s
      case 'live':
        return draft && !s.done ? { ...s, to: wizardResumePath(draft.id, 5), cta: i18n.t('aiWizard:lib.start.launch') } : s
      default:
        return s
    }
  })
}
